import { motion } from 'framer-motion';
import { MessageCircle, ArrowUp, Instagram, Linkedin, Twitter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = ({ whatsappUrl }) => {
  const { language } = useLanguage();

  const content = {
    en: {
      ctaHeadline: 'Ready to',
      ctaAccent: 'Transform',
      ctaEnd: 'Your Brand?',
      ctaSubheadline: "Join 400+ businesses that trust Sentetica.",
      ctaBtn: 'Start a Conversation',
      description: 'Digital intelligence solutions for brands that want to scale.',
      servicesTitle: 'Services',
      services: ['Meta Ads', 'AI Video', 'Web Design', 'Branding'],
      companyTitle: 'Company',
      company: ['Portfolio', 'Why Us', 'Contact'],
      whatsappUs: 'WhatsApp Us',
      copyright: 'All rights reserved.',
    },
    tr: {
      ctaHeadline: 'Markanızı',
      ctaAccent: 'Dönüştürmeye',
      ctaEnd: 'Hazır mısınız?',
      ctaSubheadline: "Sentetica'ya güvenen 400+ işletmeye katılın.",
      ctaBtn: 'Sohbet Başlatın',
      description: 'Büyümek isteyen markalar için dijital zeka çözümleri.',
      servicesTitle: 'Hizmetler',
      services: ['Meta Reklamlar', 'AI Video', 'Web Tasarım', 'Marka'],
      companyTitle: 'Şirket',
      company: ['Portfolyo', 'Neden Biz', 'İletişim'],
      whatsappUs: "WhatsApp'tan Yazın",
      copyright: 'Tüm hakları saklıdır.',
    },
  };

  const t = content[language] || content.en;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0A0A0A] text-white pt-16 pb-6">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* CTA */}
        <div className="text-center pb-12 border-b border-white/10">
          <h3 className="font-heading text-2xl md:text-3xl font-bold mb-3">
            {t.ctaHeadline} <span className="text-[#E60000]">{t.ctaAccent}</span> {t.ctaEnd}
          </h3>
          <p className="text-gray-400 text-sm mb-6">{t.ctaSubheadline}</p>
          <motion.a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#E60000] text-white px-6 py-3 rounded-full font-bold shadow-lg hover:bg-[#CC0000] transition-all duration-300 hover:-translate-y-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="footer-whatsapp-btn"
          >
            <MessageCircle size={18} />
            {t.ctaBtn}
          </motion.a>
        </div>

        {/* Links */}
        <div className="grid md:grid-cols-4 gap-8 py-12">
          <div className="md:col-span-2">
            <a href="#" className="font-heading text-xl font-black">
              Sente<span className="text-[#E60000]">tica</span>
            </a>
            <p className="text-gray-400 text-sm mt-3 max-w-xs">{t.description}</p>
            <div className="flex gap-3 mt-4">
              {[Instagram, Linkedin, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#E60000] transition-colors duration-300"
                  data-testid={`social-link-${i}`}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-gray-400 mb-3">{t.servicesTitle}</h4>
            <ul className="space-y-2">
              {t.services.map((item) => (
                <li key={item}>
                  <a href="#services" className="text-gray-300 text-sm hover:text-white transition-colors">
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-gray-400 mb-3">{t.companyTitle}</h4>
            <ul className="space-y-2">
              {t.company.map((item, i) => (
                <li key={item}>
                  <a href={['#portfolio', '#why-us', '#contact'][i]} className="text-gray-300 text-sm hover:text-white transition-colors">
                    {item}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#25D366] font-bold text-sm mt-3"
              data-testid="footer-whatsapp-link"
            >
              <MessageCircle size={14} />
              {t.whatsappUs}
            </a>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-white/10 flex justify-between items-center">
          <p className="text-gray-500 text-xs">© {new Date().getFullYear()} Sentetica. {t.copyright}</p>
          <button
            onClick={scrollToTop}
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#E60000] transition-colors duration-300"
            data-testid="scroll-to-top-btn"
          >
            <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
